import {
  ApplicationRef,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  createComponent,
  inject
} from '@angular/core';

import { Observable, Subject, take } from 'rxjs';

import { TOAST_CONFIG } from './toast-config';
import { ToastContainerComponent } from './toast-container/toast-container.component';
import { ToastRef as ToastRefAdapter, ToastRefAfterClosed } from './toast-ref';
import { ToastPosition, ToastVariant } from './toast.component';

interface ToastProps {
  id?: string;
  variant?: ToastVariant;
  position?: ToastPosition;
  showIcon?: boolean;
  className?: string;
  duration?: number;
  dismissible?: boolean;
  autoClose?: boolean;
}

export type Toast = ToastProps & {
  message: string;
  offset?: number;
  /** Auto-close stagger index among open toasts at the same position. */
  stackIndex?: number;
  instanceId: string;
  toastRef: ToastRefAdapter;
};

type ToastRef = Omit<
  ToastRefAdapter,
  'setCloseHandler' | 'setOnClosing' | 'notifyClosing'
>;

interface ToastRefCloseOptions {
  dismissedByAction?: boolean;
}

/**
 * Public handle returned when `show` is deferred (e.g. re-entrancy during
 * close/eviction). Buffers `close` until a real toast is attached, or completes
 * `afterClosed$` without mounting if closed beforehand.
 */
class DeferredToastRef {
  #attached: ToastRef | null = null;
  #closedBeforeAttach = false;
  #pendingClose: ToastRefCloseOptions | null = null;
  #afterClosed$ = new Subject<ToastRefAfterClosed>();
  #afterClosedBridged = false;
  #instanceId: string;

  constructor(private readonly id: string) {
    this.#instanceId = id;
  }

  get closedBeforeAttach(): boolean {
    return this.#closedBeforeAttach;
  }

  asPublicRef(): ToastRef {
    const ref = {
      afterClosed$: () => this.#resolveAfterClosed$(),
      close: (options?: ToastRefCloseOptions) => this.#close(options)
    } as ToastRef;

    Object.defineProperty(ref, 'instanceId', {
      enumerable: true,
      get: () => this.#instanceId
    });

    return ref;
  }

  attach(ref: ToastRef): void {
    if (this.#closedBeforeAttach) {
      return;
    }

    this.#attached = ref;
    this.#instanceId = ref.instanceId;
    this.#bridgeAfterClosed(ref);

    if (this.#pendingClose) {
      ref.close(this.#pendingClose);
      this.#pendingClose = null;
    }
  }

  #resolveAfterClosed$(): Observable<ToastRefAfterClosed> {
    // Always expose the deferred Subject so early subscribers stay notified
    // after attach. Delegating to `attached.afterClosed$()` would cancel the
    // bridge subscription (ToastRef tears down the previous one on each call).
    return this.#afterClosed$.asObservable().pipe(take(1));
  }

  #close(options: ToastRefCloseOptions = {}): boolean {
    if (this.#attached) {
      return this.#attached.close(options);
    }

    this.#closedBeforeAttach = true;
    this.#pendingClose = options;

    if (!this.#afterClosed$.closed) {
      this.#afterClosed$.next({
        dismissedByAction: options.dismissedByAction ?? false,
        id: this.id,
        instanceId: this.#instanceId
      });
      this.#afterClosed$.complete();
    }

    return false;
  }

  #bridgeAfterClosed(ref: ToastRef): void {
    if (this.#afterClosedBridged || this.#afterClosed$.closed) {
      return;
    }

    this.#afterClosedBridged = true;
    let gotValue = false;

    ref.afterClosed$().subscribe({
      next: (value) => {
        gotValue = true;
        if (!this.#afterClosed$.closed) {
          this.#afterClosed$.next(value);
          this.#afterClosed$.complete();
        }
      },
      error: (error) => {
        if (!this.#afterClosed$.closed) {
          this.#afterClosed$.error(error);
        }
      },
      complete: () => {
        // Ref already closed before attach — complete deferred without hanging.
        if (!gotValue && !this.#afterClosed$.closed) {
          this.#afterClosed$.next({
            dismissedByAction: false,
            id: this.id,
            instanceId: ref.instanceId
          });
          this.#afterClosed$.complete();
        }
      }
    });
  }
}

interface PendingShow {
  message: string;
  props: ToastProps;
  existingRef?: ToastRefAdapter;
  deferred: DeferredToastRef;
}

const MAX_TOASTS_DEFAULT = 5;
const DEFAULT_TOAST_OFFSET_HEIGHT = 64;
const TOAST_STACK_OVERLAP_PX = 16;

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  readonly #config = inject(TOAST_CONFIG);

  private toasts: Toast[] = [];
  private toastContainerRef: ComponentRef<ToastContainerComponent> | null =
    null;

  #updateDepth = 0;
  #pendingShows: PendingShow[] = [];
  #flushScheduled = false;
  /** ToastRefs currently playing exit animation (still in `toasts`). */
  #closingRefs = new Set<ToastRefAdapter>();

  constructor(private appRef: ApplicationRef) {}

  /**
   * Displays a toast message with the specified properties.
   * This method creates a new toast with the given message and properties,
   * or updates an existing toast if one with the same ID already exists.
   * If the maximum number of toasts is reached, the oldest toast will be removed.
   * @param message The message to display in the toast.
   * @param props Optional properties to customize the toast.
   * @returns A reference to the created toast.
   * @example
   * ```typescript
   * this.toastService.show('This is a toast message', {
   *   variant: 'success',
   *   position: 'top-right',
   *   duration: 4000,
   *   dismissible: true,
   *   autoClose: true
   * });
   * ```
   */
  show(message: string, props: ToastProps = {}): ToastRef {
    if (this.#updateDepth > 0) {
      return this.#enqueueShow(message, props);
    }

    return this.#show(message, props);
  }

  #enqueueShow(
    message: string,
    props: ToastProps = {},
    existingRef?: ToastRefAdapter
  ): ToastRef {
    const propsWithId: ToastProps = {
      ...props,
      id: props.id || this.#generateToastId()
    };
    const deferred = new DeferredToastRef(propsWithId.id!);

    this.#pendingShows.push({
      message,
      props: propsWithId,
      existingRef,
      deferred
    });

    return deferred.asPublicRef();
  }

  #beginUpdate(): void {
    this.#updateDepth++;
  }

  #endUpdate(): void {
    this.#updateDepth--;

    // Defer flush so the synchronous `show()` caller receives an open ref
    // before nested pending shows (e.g. from eviction afterClosed$) can
    // evict that toast under maxCount.
    if (this.#updateDepth === 0 && !this.#flushScheduled) {
      this.#flushScheduled = true;
      queueMicrotask(() => {
        this.#flushScheduled = false;

        if (this.#updateDepth === 0) {
          this.#flushPendingShows();
        }
      });
    }
  }

  #flushPendingShows(): void {
    if (this.#pendingShows.length === 0) {
      return;
    }

    const queue = this.#pendingShows;
    this.#pendingShows = [];

    for (const item of queue) {
      if (item.deferred.closedBeforeAttach) {
        continue;
      }

      const ref = this.#show(item.message, item.props, item.existingRef);

      if (item.deferred.closedBeforeAttach) {
        // Mount raced with close-before-attach — drop immediately (no enter
        // flash / maxCount occupancy). Animated `close()` would keep it
        // visible for ~150ms.
        const toastId = item.props.id;
        const mounted = toastId ? this.#findOpenToastById(toastId) : undefined;
        if (mounted) {
          this.#dismissFromContainer(mounted.instanceId, false);
        } else {
          ref.close({ dismissedByAction: false });
        }
        continue;
      }

      item.deferred.attach(ref);
    }
  }

  #show(
    message: string,
    props: ToastProps = {},
    existingRef?: ToastRefAdapter
  ): ToastRef {
    this.#beginUpdate();

    try {
      const { id, ...restOfProps } = props || {};
      const toastId = id || this.#generateToastId();
      const toastRef = this.#resolveToastRefForShow(toastId, existingRef);

      this.#evictUntilUnderMax();

      const rejected = this.#rejectIfAtMax(toastId, toastRef);
      if (rejected) {
        return rejected;
      }

      return this.#mountToast(toastId, message, restOfProps, toastRef);
    } finally {
      this.#endUpdate();
    }
  }

  #resolveToastRefForShow(
    toastId: string,
    existingRef?: ToastRefAdapter
  ): ToastRefAdapter {
    const toastAlreadyOpen = this.#findOpenToastById(toastId);

    if (toastAlreadyOpen) {
      const replacementRef =
        existingRef ?? new ToastRefAdapter(toastId, this.#generateInstanceId());
      this.close(toastAlreadyOpen.id!, false);
      return replacementRef;
    }

    return (
      existingRef ?? new ToastRefAdapter(toastId, this.#generateInstanceId())
    );
  }

  #findOpenToastById(toastId: string): Toast | undefined {
    return this.toasts.find(
      (toast) => toast.id === toastId && !this.#closingRefs.has(toast.toastRef)
    );
  }

  #openToastCount(): number {
    return this.toasts.filter(
      (toast) => toast.id && !this.#closingRefs.has(toast.toastRef)
    ).length;
  }

  #evictUntilUnderMax(): void {
    const maxCount = this.#config.maxCount || MAX_TOASTS_DEFAULT;

    while (this.#openToastCount() >= maxCount) {
      const oldestToast = this.toasts.find(
        (toast) => toast.id && !this.#closingRefs.has(toast.toastRef)
      );

      if (!oldestToast?.id) {
        break;
      }

      console.warn(
        `Toast com ID "${oldestToast.id}" removido para abrir espaço.`
      );
      this.close(oldestToast.id, false);
    }
  }

  #rejectIfAtMax(toastId: string, toastRef: ToastRefAdapter): ToastRef | null {
    const maxCount = this.#config.maxCount || MAX_TOASTS_DEFAULT;

    if (this.#openToastCount() < maxCount) {
      return null;
    }

    console.warn(
      `Toast "${toastId}" não aberto: limite de ${maxCount} atingido.`
    );
    toastRef.close({ dismissedByAction: false });

    return {
      instanceId: toastRef.instanceId,
      afterClosed$: () => toastRef.afterClosed$(),
      close: (options) => toastRef.close(options)
    };
  }

  #mountToast(
    toastId: string,
    message: string,
    restOfProps: Omit<ToastProps, 'id'>,
    toastRef: ToastRefAdapter
  ): ToastRef {
    toastRef.setOnClosing(() => {
      this.#closingRefs.add(toastRef);
    });

    const toastProps = this.#createToastProps(restOfProps);
    const position = toastProps.position || 'top-center';
    const newToast: Toast = {
      id: toastId,
      instanceId: toastRef.instanceId,
      message,
      offset: this.#getOffsetHeightFromLastToast(position),
      stackIndex: this.#openStackIndex(position),
      toastRef,
      ...toastProps
    };

    this.toasts = [...this.toasts, newToast];
    this.#renderToastContainer();

    return {
      instanceId: toastRef.instanceId,
      afterClosed$: () => toastRef.afterClosed$(),
      close: (options) =>
        this.#closeToastRef(toastRef, options?.dismissedByAction ?? false)
    };
  }

  /** Count of open (non-closing) toasts already stacked at `position`. */
  #openStackIndex(position: ToastPosition): number {
    return this.toasts.filter(
      (toast) =>
        (toast.position || 'top-center') === position &&
        !this.#closingRefs.has(toast.toastRef)
    ).length;
  }

  success(message: string, props: Omit<ToastProps, 'variant'> = {}): ToastRef {
    return this.show(message, {
      ...props,
      variant: 'success'
    });
  }

  danger(message: string, props: Omit<ToastProps, 'variant'> = {}): ToastRef {
    return this.show(message, {
      ...props,
      variant: 'danger'
    });
  }

  warning(message: string, props: Omit<ToastProps, 'variant'> = {}): ToastRef {
    return this.show(message, {
      ...props,
      variant: 'warning'
    });
  }

  info(message: string, props: Omit<ToastProps, 'variant'> = {}): ToastRef {
    return this.show(message, {
      ...props,
      variant: 'info'
    });
  }

  /**
   * Starts the exit animation (same path as public `ToastRef.close` on main).
   * The toast stays in `toasts` until the component emits `closed`, then
   * `#dismissFromContainer` removes it. If no component handler is attached,
   * removes immediately.
   * @returns whether the exit animation started
   */
  close(id: string, dismissedByAction = false): boolean {
    const toast = this.#findOpenToastById(id);

    if (!toast) {
      return false;
    }

    return this.#closeToastRef(toast.toastRef, dismissedByAction);
  }

  /**
   * Closes a specific toast instance by identity. Stale refs from a replaced
   * same-id toast are no-ops and do not close the replacement.
   */
  #closeToastRef(
    toastRef: ToastRefAdapter,
    dismissedByAction = false
  ): boolean {
    this.#beginUpdate();

    try {
      const toast = this.toasts.find((item) => item.toastRef === toastRef);

      if (!toast || this.#closingRefs.has(toastRef)) {
        return false;
      }

      this.#closingRefs.add(toastRef);
      const animated = toastRef.close({ dismissedByAction });

      if (!animated) {
        this.#removeToastFromList(toast, dismissedByAction);
      }

      return animated;
    } finally {
      this.#endUpdate();
    }
  }

  /**
   * Finalizer after exit animation (container `closed` output) or for
   * immediate teardown when animation must be skipped.
   */
  #dismissFromContainer(instanceId: string, dismissedByAction = false): void {
    this.#beginUpdate();

    try {
      const toast = this.toasts.find((item) => item.instanceId === instanceId);

      if (!toast) {
        return;
      }

      this.#removeToastFromList(toast, dismissedByAction);
    } finally {
      this.#endUpdate();
    }
  }

  #removeToastFromList(toast: Toast, dismissedByAction: boolean): void {
    this.toasts = this.toasts.filter((item) => item !== toast);
    this.#closingRefs.delete(toast.toastRef);
    toast.toastRef.setOnClosing(null);
    this.#renderToastContainer();
    this.#recalculateToastOffsets();
    this.#renderToastContainer();

    // User dismiss / auto-close call this path without prior toastRef.close.
    toast.toastRef.close({ dismissedByAction });
  }

  #getToastHeight(instanceId: string): number {
    const toastContainer = document.querySelector(
      `[data-toast-container-id="container-${instanceId}"]`
    ) as HTMLElement | null;

    const measuredHeight = toastContainer?.offsetHeight ?? 0;

    return measuredHeight > 0 ? measuredHeight : DEFAULT_TOAST_OFFSET_HEIGHT;
  }

  #getCumulativeStackOffset(toasts: Toast[]): number {
    return toasts.reduce((sum, toast) => {
      if (!toast.instanceId) {
        return sum;
      }

      return (
        sum + this.#getToastHeight(toast.instanceId) - TOAST_STACK_OVERLAP_PX
      );
    }, 0);
  }

  #recalculateToastOffsets(): void {
    if (this.toastContainerRef) {
      this.toastContainerRef.changeDetectorRef.detectChanges();
    }

    const cumulativeByPosition = new Map<ToastPosition, number>();

    this.toasts = this.toasts.map((toast) => {
      const position = toast.position || 'top-center';
      const cumulative = cumulativeByPosition.get(position) ?? 0;
      const updated = { ...toast, offset: cumulative };

      if (toast.instanceId) {
        cumulativeByPosition.set(
          position,
          cumulative +
            this.#getToastHeight(toast.instanceId) -
            TOAST_STACK_OVERLAP_PX
        );
      }

      return updated;
    });
  }

  #getOffsetHeightFromLastToast(position: ToastPosition): number {
    const samePositionToasts = this.toasts.filter(
      (toast) => (toast.position || 'top-center') === position
    );

    if (samePositionToasts.length === 0) {
      return 0;
    }

    if (this.toastContainerRef) {
      this.toastContainerRef.changeDetectorRef.detectChanges();
    }

    return this.#getCumulativeStackOffset(samePositionToasts);
  }

  #renderToastContainer(): void {
    if (!this.toastContainerRef) {
      this.toastContainerRef = createComponent(ToastContainerComponent, {
        environmentInjector: this.appRef.injector
      });

      this.appRef.attachView(this.toastContainerRef.hostView);
      const domElem = (
        this.toastContainerRef.hostView as EmbeddedViewRef<unknown>
      ).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
    }

    this.toastContainerRef.setInput('toasts', this.toasts);
    this.toastContainerRef.setInput(
      'onToastClosed',
      (instanceId: string, dismissedByAction: boolean) =>
        this.#dismissFromContainer(instanceId, dismissedByAction)
    );
    this.toastContainerRef.changeDetectorRef.detectChanges();
  }

  #createToastProps(props: Omit<ToastProps, 'id'>): ToastProps {
    return {
      variant: props.variant || this.#config.defaultVariant || 'warning',
      position: props.position || this.#config.defaultPosition || 'top-center',
      showIcon: props.showIcon ?? true,
      className: props.className || '',
      duration: props.duration || this.#config.defaultDuration || 4000,
      dismissible: props.dismissible ?? false,
      autoClose: props.autoClose ?? this.#config.autoClose ?? true
    };
  }

  #generateToastId(): string {
    return `toast-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }

  #generateInstanceId(): string {
    return `toast-instance-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }
}
